import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "resumeflow_onboarding";

interface OnboardingState {
  selectorCompleted: boolean;
  builderCompleted: boolean;
}

function getState(userId?: string): OnboardingState {
  try {
    const key = userId ? `${STORAGE_KEY}_${userId}` : STORAGE_KEY;
    const raw = localStorage.getItem(key);
    if (!raw) return { selectorCompleted: false, builderCompleted: false };

    const parsed = JSON.parse(raw);
    return {
      selectorCompleted: Boolean(parsed?.selectorCompleted),
      builderCompleted: Boolean(parsed?.builderCompleted),
    };
  } catch {
    return { selectorCompleted: false, builderCompleted: false };
  }
}

function saveState(state: OnboardingState, userId?: string) {
  const key = userId ? `${STORAGE_KEY}_${userId}` : STORAGE_KEY;
  localStorage.setItem(key, JSON.stringify(state));
}

export function resetTourForUser(userId: string) {
  const key = `${STORAGE_KEY}_${userId}`;
  localStorage.removeItem(key);
  // Cleanup legacy global key so replay works reliably
  localStorage.removeItem(STORAGE_KEY);
}

export function useOnboardingTour(phase: "selector" | "builder") {
  const { user, isLoading } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  // Stable user ID to avoid re-triggering on object reference changes
  const userId = user?.id ?? null;

  // Reset check flag when user ID actually changes
  useEffect(() => {
    if (lastUserIdRef.current !== userId) {
      lastUserIdRef.current = userId;
      checkedRef.current = false;
    }
  }, [userId]);

  useEffect(() => {
    if (isLoading) return;
    if (!userId) return;
    if (checkedRef.current) return;

    const state = getState(userId);
    const key = phase === "selector" ? "selectorCompleted" : "builderCompleted";

    if (state[key]) {
      checkedRef.current = true;
      return;
    }

    let cancelled = false;

    const checkUserResumes = async () => {
      try {
        const { count, error } = await supabase
          .from("saved_resumes")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId);

        if (cancelled) return;
        checkedRef.current = true;

        if (!error && (count ?? 0) > 0) {
          // Returning user — mark completed
          saveState({ ...state, selectorCompleted: true, builderCompleted: true }, userId);
        } else {
          // New user — show tour after a short delay for DOM to render
          timerRef.current = setTimeout(() => {
            if (!cancelled) {
              setIsActive(true);
            }
          }, 1000);
        }
      } catch {
        if (cancelled) return;
        checkedRef.current = true;
        timerRef.current = setTimeout(() => {
          if (!cancelled) {
            setIsActive(true);
          }
        }, 1000);
      }
    };

    void checkUserResumes();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [phase, userId, isLoading]);

  const next = useCallback(() => setCurrentStep((s) => s + 1), []);
  const prev = useCallback(() => setCurrentStep((s) => Math.max(0, s - 1)), []);

  const finish = useCallback(() => {
    const state = getState(userId ?? undefined);
    const key = phase === "selector" ? "selectorCompleted" : "builderCompleted";
    saveState({ ...state, [key]: true }, userId ?? undefined);
    setIsActive(false);
    setCurrentStep(0);
  }, [phase, userId]);

  const skip = finish;

  // Allow manual trigger for "Take a tour" button
  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  return { isActive, currentStep, setCurrentStep, next, prev, finish, skip, startTour };
}
